<template>
  <v-container>
    <v-card flat>
      <AddAccountPrimaryFields :isLoading="loading" @cancel="onCancel" @save="onSave" />
    </v-card>

    <v-snackbar v-model="snackbar.show" :color="snackbar.color" timeout="3000" location="top">
      {{ snackbar.message }}
      <template v-slot:actions>
        <v-btn variant="text" @click="snackbar.show = false">Close</v-btn>
      </template>
    </v-snackbar>
  </v-container>
</template>

<script setup lang="ts">
import { useCreateAccountStore } from '@/stores/createAccountStore'
import AddAccountPrimaryFields from '@/components/AddAccountPrimaryFields.vue'
import { useRouter } from 'vue-router'
import { useAccountStore } from '@/stores/accountStore'
import { createAccount } from '@/api/accountsAdmin'
import { ref } from 'vue'
import { getRequiredBillingErrors, trimRequiredBillingFields } from '@/utils/billingValidation'

const store = useCreateAccountStore()
const formFields = store.formFields
const router = useRouter()
const accountStore = useAccountStore()
const loading = ref(false)
const urlRule = (v: string) => {
  if (!v) return true
  const pattern = /^(https?:\/\/)[^\s/$.?#].[^\s]*$/i
  return pattern.test(v) || 'Please enter a valid URL (must start with http:// or https://)'
}

const validationErrors = ref<string[]>([])
const resetFormFields = () => {
  store.formFields = {
    accountName: '',
    accountNameFriendly: '',
    main_website: '',
    a5Account: 0,
    initials: '',
    stateCode: '',
    countryCode: 'US',
    clientId: '',
    parentAccount: '',
    isParent: true,
    image: '',
    timezone: '',
    dealerPA: '',
    accountType: 'unspecified',
    accountTypeFord: false,

    primaryContact: {
      name: '',
      email: '',
      phone: '',
    },
    billing: {
      name: '',
      address_1: '',
      address_2: '',
      city: '',
      state: '',
      zip: '',
    },
    notes: '',
    address: '',
  }
}
const snackbar = ref({
  show: false,
  message: '' as string,
  color: 'error' as 'error' | 'success' | 'info',
})
const onCancel = () => {
  router.push('/account-admin')
}
const onSave = () => {
  submitForm()
}
const validateForm = () => {
  validationErrors.value = []
  const {
    accountName,
    main_website,
    stateCode,
    countryCode,
    parentAccount,
    initials,
    dealerPA,
    accountType,
    billing,
  } = formFields
  if (!initials) validationErrors.value.push('Initials is required.')
  if (!countryCode) validationErrors.value.push('Country is required.')
  if (!stateCode) validationErrors.value.push('State is required.')
  if (!accountName) validationErrors.value.push('Account Name is required.')
  if (!parentAccount) validationErrors.value.push('Parent Account is Required.')
  validationErrors.value.push(...getRequiredBillingErrors(billing))
  const urlCheck = urlRule(main_website)
  if (urlCheck !== true) {
    validationErrors.value.push(urlCheck as string)
  }
  if (accountType === 'Ford Direct') {
    if (!dealerPA) {
      validationErrors.value.push('Dealer PA is required.')
    }
  } else {
    if (!accountType) {
      validationErrors.value.push('Account Type is required.')
    }
  }
  return validationErrors.value.length === 0
}

const submitForm = async () => {
  if (!validateForm()) {
    snackbar.value.message = 'Please fill all the required fields'
    snackbar.value.color = 'error'
    snackbar.value.show = true
    return
  }
  if (loading.value) return
  loading.value = true
  try {
    const payload = preparePayload(store.formFields)

    // Call the API and capture the response with JUID
    const response = await createAccount(payload)
    console.log('Account creation response:', response)

    // Extract JUID from response (fallback to accountName if not present)
    const juid = response?.juid || payload.accountName

    snackbar.value.message = '✅ Account Created successfully!'
    snackbar.value.color = 'success'
    snackbar.value.show = true
    setTimeout(() => {
      try {
        accountStore.setAccountsCache([])
      } catch {}
      // Redirect to JUID instead of accountName
      router.push(`/account-admin/${juid}`)
    }, 1200)
    resetFormFields()
  } catch (error: any) {
    console.error('Error creating account:', error)
    snackbar.value.message = 'Failed to create account. Please try again.'
    snackbar.value.color = 'error'
    snackbar.value.show = true
  } finally {
    loading.value = false
  }
}

const preparePayload = (formFields: any) => {
  const adjustedPayload = {
    ...formFields,
    billing: trimRequiredBillingFields(formFields.billing),
  }
  if (adjustedPayload.image) {
    adjustedPayload.image = adjustedPayload.image
  }
  delete adjustedPayload.step
  delete adjustedPayload.accountTypeFord
  adjustedPayload.accountName = adjustedPayload.accountName.toLowerCase().replace(/[-\s]/g, '_')
  return adjustedPayload
}
</script>

<style scoped>
h1 {
  margin-bottom: 20px;
}
.mt-4 {
  margin-right: 1px;
  margin-bottom: 1px;
}
.cancel {
  background-color: #b61431;
  color: white !important;
}
</style>
